import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';

/**
 * Calendar component that allows selecting dates and date ranges.
 *
 * @class FlameCalendarComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'ta-flame-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    standalone: false
})
export class FlameCalendarComponent implements OnInit {
  @ViewChild( 'yearsUl' ) yearsUl: ElementRef<HTMLUListElement>;
  @ViewChild( 'focusElement' ) elementFocus: ElementRef<HTMLElement>;

  /**
   * Defines if the calendar allows selecting a date range.
   *
   * @type {boolean}
   * @default false
   */
  @Input() range: boolean = false;

  /**
   * Minimum selectable date in the calendar.
   *
   * @type {Date | null}
   * @default null
   */
  @Input() minimumDate: Date | null = null;

  /**
   * Maximum selectable date in the calendar.
   *
   * @type {Date | null}
   * @default null
   */
  @Input() maximumDate: Date | null = null;

  /**
   * A boolean flag indicating whether only the months should be displayed.
   *
   * @type {boolean}
   * @default true
   */
  @Input() onlyMonths: boolean = false;

  /**
   * Emits the currently selected date.
   *
   * @type {EventEmitter<Date>}
   */
  @Output() currentDateChange: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * Emits the selected start date in the range.
   *
   * @type {EventEmitter<Date>}
   */
  @Output() startDateChange: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * Emits the selected end date in the range.
   *
   * @type {EventEmitter<Date>}
   */
  @Output() endDateChange: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() focusEvent = new EventEmitter<void>();
  @Output() blurEvent = new EventEmitter<void>();

  /* Statements */
  yearsList    : number[] = [];
  showYearsList: boolean  = false;
  monthsName   : string[] = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  daysOfWeek   : string[] = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  year         : number;
  month        : number;
  calendarDays : { day:number, date:Date, currentMonth:boolean, isDay:boolean, disabled:boolean }[] = [];

  private _currentDate: Date        = new Date();
  private _startDate  : Date | null = null;
  private _endDate    : Date | null = null;

  public rangeTypeCalendarOpen: string = '';

  /**
   * The currently selected date in the calendar.
   *
   * @type {Date}
   */
  @Input()
  set currentDate( value: Date ) {
    //if ( value!==this._currentDate ) {
      this._currentDate = value;
      this.year         = this._currentDate?.getFullYear() || 0;
      this.month        = this._currentDate?.getMonth   () || 0;

      this.generateCalendar();
    //}
  }
  get currentDate(): Date {
    return this._currentDate;
  }

  /**
   * The selected start date in the range.
   *
   * @type {Date | null}
   */
  @Input()
  set startDate( value: Date | null ) {
    //if ( value!==this._startDate ) {
      this._startDate = value;
      this.generateCalendar();
    //}
  }
  get startDate(): Date | null {
    return this._startDate;
  }

  /**
   * The selected end date in the range.
   *
   * @type {Date | null}
   */
  @Input()
  set endDate( value: Date | null ) {
    if ( value!==this._endDate ) {
      this._endDate = value;

      this.generateCalendar();
    }
  }
  get endDate(): Date | null {
    return this._endDate;
  }

  constructor() {
    this.year  = this.currentDate.getFullYear();
    this.month = this.currentDate.getMonth();
  }

  /**
   * Initializes the component by generating the calendar and the years list.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.generateCalendar();
    this.generateYears();
  }

  /**
   * Detects changes in input properties and updates the calendar and years list.
   *
   * @returns {void}
   */
  ngOnChanges(): void {
    this.generateCalendar();
    this.generateYears();
  }

  /**
   * Checks if a date is within the selected range.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isInRange( date: Date ): boolean {
    if ( !this.startDate || !this.endDate ) {return false};

    return date>=this.startDate && date<=this.endDate;
  }

  /**
   * Checks if a date is the first date in the selected range.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isFirst( date: Date ) {
    if ( this.range && this.startDate && this.endDate && date>=this.startDate && date<=this.endDate ) {
      return date.getTime()===this.startDate.getTime();
    }
  }

  /**
   * Checks if a date is the last date in the selected range.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isLast( date: Date ) {
    if ( this.range && date>=this.startDate && date<=this.endDate ) {
      return date.getTime()===this.endDate.getTime();
    }
  }

  /**
   * Checks if a date is disabled.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isDateDisabled( date: Date ): boolean {
    return (
      ( this._isDateLessOrEqualToday(date) )
    );
  }

  private _isDateLessOrEqualToday(dateToCheck: Date): boolean {
    const today = new Date();
    // Limpiar la hora para comparar solo la fecha
    today.setHours(0, 0, 0, 0);
    const check = new Date(dateToCheck);
    check.setHours(0, 0, 0, 0);
    return check.getTime() <= today.getTime();
  }

  /**
   * Checks if a date is selected.
   *
   * @param {Date} date - The date to check.
   * @returns {boolean}
   */
  isSelectedDate( date: Date ): boolean {
    const datePipe = new DatePipe('es-MX');
    const dateString = datePipe.transform(date, 'yyyy-MM-dd');
    const startDateString = datePipe.transform(this.startDate, 'yyyy-MM-dd');
    const endDateString = datePipe.transform(this.startDate, 'yyyy-MM-dd');
    return dateString === startDateString || dateString === endDateString;
  }

  /**
   * Generates the list of selectable years in the calendar.
   *
   * @returns {void}
   */
  generateYears() {
    const minYear  = this.minimumDate ? this.minimumDate.getFullYear(): 1900;
    const maxYear  = this.maximumDate ? this.maximumDate.getFullYear(): 3000;
    this.yearsList = Array.from({ length: maxYear - minYear + 1 }, ( _, i ) => minYear + i );
  }

  /**
   * Generates the calendar days for the selected month and year.
   *
   * @returns {void}
   */
  generateCalendar() {
    this.calendarDays         = [];
    const dateActual          = new Date(                              );
    const firstDayOfMonth     = new Date( this.year, this.month    , 1 ).getDay ();
    const daysInCurrentMonth  = new Date( this.year, this.month + 1, 0 ).getDate();
    const daysInPreviousMonth = new Date( this.year, this.month    , 0 ).getDate();

    dateActual.setHours( 0, 0, 0, 0 );

    // Mes anterior
    for ( let i=firstDayOfMonth; i>0; i-- ) {
      const date     = new Date( this.year, this.month - 1, daysInPreviousMonth - i + 1 );
      const disabled = this.isDateDisabled( date );

      this.calendarDays.push({ date, disabled, day:( daysInPreviousMonth - i + 1 ), currentMonth:false, isDay:false });
    }

    // Mes actual
    for ( let i=1; i<=daysInCurrentMonth; i++ ) {
      const date     = new Date( this.year, this.month, i );
      const isDay    = dateActual.getTime()===date.getTime();
      const disabled = this.isDateDisabled( date );

      this.calendarDays.push({ date, isDay, disabled, day:i, currentMonth:true });
    }

    // Mes siguiente
    const remainingDaysInWeek = 7 - ( this.calendarDays.length % 7 );

    for ( let i=1; remainingDaysInWeek<7 && i<=remainingDaysInWeek; i++ ) {
      const date     = new Date(this.year, this.month + 1, i);
      const disabled = this.isDateDisabled(date);

      this.calendarDays.push({ date, disabled, day:i, currentMonth:false, isDay:false });
    }
  }

  /**
   * Updates the currently selected date.
   *
   * @returns {void}
   */
  updateCurrentDate( day?:number ) {
    this.currentDate = new Date( this.year, this.month, day ?? this.currentDate.getDate() );
  }

  /**
   * Switches to the previous month in the calendar.
   *
   * @returns {void}
   */
  onPreviousMonth() {
    if ( this.month===0 ) {
      this.month = 11;
      this.year--;
    }
    else {
      this.month--
    };

    this.updateCurrentDate();
    this.generateCalendar();
  }

  /**
   * Switch
   * Switches to the next month in the calendar.
   *
   * @returns {void}
   */
  onNextMonth() {
    if ( this.month===11 ) {
      this.month = 0;
      this.year++;
    }
    else {
      this.month++;
    }

    this.updateCurrentDate();
    this.generateCalendar();
  }

  /**
   * Selects a date in the calendar.
   *
   * @param {Date} date - The date to select.
   * @returns {void}
   */
  onSelectDate( date: Date ) {
    if ( this.isDateDisabled( date ) ) {return};

    if ( !this.range ) {
      this.startDate = date;
      this.endDate   = null;
    }
    else {
      this.setDates(date);
    }

    this.currentDate = date;

    this.currentDateChange.emit( this.currentDate );
    this.startDateChange  .emit( this.startDate   );
    this.endDateChange    .emit( this.endDate     );
  }

  /**
   * Handles the selection of a new month and updates the calendar view.
   *
   * @param {number} new_month - The newly selected month, represented as an integer (1-12).
   *
   * @returns {void}
   */
  onSelectMonth(new_month:number) {
    this.month = new_month;

    this.updateCurrentDate( 1 );
    this.generateCalendar();
    this.onSelectDate( this.currentDate );
  }

  /**
   * Sets the dates range.
   *
   * @param {Date} date - The date to select.
   * @returns {void}
   */
  setDates(date: Date): void {
    const type_open            = this.rangeTypeCalendarOpen;
    this.rangeTypeCalendarOpen = '';

    if ( !this.startDate && !this.endDate ) {
      this.startDate = date;
      this.endDate   = null;

      return;
    }

    if ( this.startDate && !this.endDate ) {
      this.endDate = date;

      return;
    }

    if ( !this.startDate && this.endDate ) {
      this.startDate = date;

      return;
    }

    if ( type_open==='start' ) {
      this.startDate = date

      return;
    }

    if ( type_open==='end' ) {
      this.endDate = date;

      return;
    }

    if ( this.startDate && this.endDate ) {
      this.startDate = date;
      this.endDate   = null;

      return;
    }
  }

  /**
   * Selects a year from the years list.
   *
   * @param {number} year - The year to select.
   * @returns {void}
   */
  onSelectYear( year: number ) {
    this.year          = year;
    this.showYearsList = false;

    this.updateCurrentDate();
    this.generateCalendar();
  }

  /**
   * Toggles the visibility of the years list.
   *
   * @returns {void}
   */
  onToggleYearsList() {
    this.showYearsList = !this.showYearsList;

    if ( this.showYearsList ) {
      setTimeout(
        () => {
          let   selectedYearIndex = this.yearsList.indexOf( this.year );
          const yearElement       = this.yearsUl.nativeElement.children[selectedYearIndex];

          yearElement && yearElement.scrollIntoView({ behavior:'auto', block:'center' });
        }
      );
    }
  }

  onFocus() {
    this.focusEvent.emit();
  }
  onBlur() {
    this.blurEvent.emit();
  }
  focusElement(){
    setTimeout(
      () => {
        this.elementFocus?.nativeElement.focus();
      }
    )
  }
}
